import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Star, Flame, Award, Gift, Crown, Medal,
  TrendingUp, Loader2, Zap, Calendar
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { pageTransition, fadeUp } from "@/lib/animations";
import api from "@/lib/api";
import { toast } from "sonner";

function RankBadge({ rank }) {
  if (rank === 1) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFA500] flex items-center justify-center shadow-[0_0_20px_rgba(255,215,0,0.4)]">
        <Crown className="w-5 h-5 text-white" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C0C0C0] to-[#A0A0A0] flex items-center justify-center">
        <Medal className="w-5 h-5 text-white" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#CD7F32] to-[#8B4513] flex items-center justify-center">
        <Medal className="w-5 h-5 text-white" />
      </div>
    );
  }
  return (
    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
      <span className="text-sm font-bold text-slate-400">#{rank}</span>
    </div>
  );
}

function LeaderboardRow({ user, rank, isCurrentUser }) {
  const isTop3 = rank <= 3;

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.03 }}
      className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
        isCurrentUser
          ? "bg-[#00FF88]/10 border border-[#00FF88]/30"
          : isTop3
          ? "bg-white/[0.03] backdrop-blur-xl border border-white/[0.1]"
          : "bg-white/[0.03] backdrop-blur-xl border border-white/[0.08]"
      }`}
      data-testid={`leaderboard-row-${rank}`}
    >
      <RankBadge rank={rank} />

      <div className="flex items-center gap-3 flex-1 min-w-0">
        {user.profile_image_url ? (
          <img 
            src={user.profile_image_url} 
            alt={user.name}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
            <span className="text-xs font-medium text-slate-400">{user.name?.charAt(0)?.toUpperCase() || "?"}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className={`text-sm font-semibold truncate ${isCurrentUser ? "text-[#00FF88]" : "text-white"}`} style={{ fontFamily: "Manrope" }}>
            {user.name} {isCurrentUser && <span className="text-xs opacity-60">(You)</span>}
          </p>
          <p className="text-xs text-slate-500">{user.total_points?.toLocaleString()} lifetime pts</p>
        </div>
      </div>

      <div className="text-right">
        <p className={`text-lg font-bold ${isTop3 ? "text-[#00FF88]" : "text-white"}`} style={{ fontFamily: "Manrope" }}>
          {user.points?.toLocaleString()}
        </p>
        <p className="text-xs text-slate-500">this week</p>
      </div>
    </motion.div>
  );
}

function PointsHistoryItem({ item }) {
  const actionLabels = {
    vault_upload: "Vault Upload",
    voice_recording: "Voice Recording",
    video_recording: "Video Recording",
    journal_entry: "Journal Entry",
    prompt_response: "Prompt Response",
    meal_upload: "Meal Logged",
    share_to_community: "Shared to Community",
    receive_reaction: "Received Reaction",
    daily_streak_bonus: "Daily Streak",
    weekly_streak_bonus: "Weekly Bonus",
  };

  return (
    <div className="flex items-center justify-between py-2 border-b border-white/[0.06] last:border-0">
      <div>
        <p className="text-sm text-white">{actionLabels[item.action] || item.action}</p>
        <p className="text-xs text-slate-500">
          {new Date(item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </p>
      </div>
      <span className="text-sm font-bold text-emerald-400">+{item.points}</span>
    </div>
  );
}

export default function PointsPage() {
  const [myPoints, setMyPoints] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [prizes, setPrizes] = useState(null);
  const [pastWinners, setPastWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("leaderboard");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pointsRes, lbRes, prizesRes, historyRes] = await Promise.all([
        api.get("/points/me"),
        api.get("/points/leaderboard"),
        api.get("/prizes/current"),
        api.get("/prizes/history?limit=5")
      ]);
      setMyPoints(pointsRes.data);
      setLeaderboard(lbRes.data.leaderboard || []);
      setPrizes(prizesRes.data);
      setPastWinners(historyRes.data.winners || []);
    } catch {
      toast.error("Failed to load points data");
    } finally {
      setLoading(false);
    }
  };

  const currentUserId = myPoints?.history?.[0]?.user_id;

  return (
    <motion.div variants={pageTransition} initial="hidden" animate="visible" className="p-6 md:p-10 max-w-[900px] mx-auto pb-24" data-testid="points-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#00FF88]/10 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#00FF88]" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#00FF88] font-medium">Rewards</p>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Manrope" }}>
          Points & Prizes
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Earn points for everything you do. Top 3 win weekly prizes!
        </p>
      </div>

      {/* Points Summary */}
      {myPoints && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-[#00FF88]/20 to-[#00FF88]/5 border border-[#00FF88]/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-[#00FF88]" />
              <span className="text-xs text-[#00FF88]">This Week</span>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
              {myPoints.weekly_points?.toLocaleString() || 0}
            </p>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">Total Points</span>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
              {myPoints.total_points?.toLocaleString() || 0}
            </p>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-slate-500">Your Rank</span>
            </div>
            <p className="text-3xl font-bold text-white" style={{ fontFamily: "Manrope" }}>
              #{myPoints.weekly_rank || "-"}
            </p>
          </div>

          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-slate-500">Week</span>
            </div>
            <p className="text-lg font-bold text-white" style={{ fontFamily: "Manrope" }}>
              {myPoints.week_key || "-"}
            </p>
          </div>
        </div>
      )}

      {/* Prize Banner */}
      <div className="bg-gradient-to-r from-[#00FF88]/20 via-[#00FF88]/10 to-transparent border border-[#00FF88]/30 rounded-2xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-3">
          <Gift className="w-6 h-6 text-[#00FF88]" />
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Manrope" }}>
            This Week's Prizes
          </h3>
          {prizes?.is_default && (
            <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30 text-xs">Coming Soon</Badge>
          )}
        </div>
        {prizes?.announcement && (
          <p className="text-sm text-slate-300 mb-4">{prizes.announcement}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(prizes?.prizes || []).map((prize) => (
            <div
              key={prize.place}
              className={`rounded-xl p-4 border ${
                prize.place === 1
                  ? "bg-[#FFD700]/10 border-[#FFD700]/30"
                  : prize.place === 2
                  ? "bg-[#C0C0C0]/10 border-[#C0C0C0]/30"
                  : "bg-[#CD7F32]/10 border-[#CD7F32]/30"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">
                  {prize.place === 1 ? "🥇" : prize.place === 2 ? "🥈" : "🥉"}
                </span>
                <span className={`text-sm font-bold ${
                  prize.place === 1 ? "text-[#FFD700]" : prize.place === 2 ? "text-[#C0C0C0]" : "text-[#CD7F32]"
                }`}>
                  {prize.title}
                </span>
              </div>
              <p className="text-xs text-slate-400 mb-1">{prize.description}</p>
              {prize.value && prize.value !== "TBD" && (
                <p className="text-sm font-bold text-white">{prize.value}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 mb-6 bg-white/[0.02] border border-white/[0.06] rounded-xl p-1.5 w-fit overflow-x-auto">
        <button
          onClick={() => setActiveTab("leaderboard")}
          data-testid="tab-leaderboard"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "leaderboard"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Trophy className="w-3.5 h-3.5" /> Leaderboard
        </button>
        <button
          onClick={() => setActiveTab("winners")}
          data-testid="tab-winners"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "winners"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Crown className="w-3.5 h-3.5" /> Past Winners
        </button>
        <button
          onClick={() => setActiveTab("history")}
          data-testid="tab-history"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "history"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Calendar className="w-3.5 h-3.5" /> Your History
        </button>
        <button
          onClick={() => setActiveTab("earn")}
          data-testid="tab-earn"
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
            activeTab === "earn"
              ? "bg-[#00FF88]/10 text-[#00FF88] border border-[#00FF88]/20"
              : "text-slate-500 hover:text-white hover:bg-white/5"
          }`}
        >
          <Zap className="w-3.5 h-3.5" /> How to Earn
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <Loader2 className="w-8 h-8 text-[#00FF88] animate-spin" />
          <p className="text-sm text-slate-400">Loading...</p>
        </div>
      ) : activeTab === "leaderboard" ? (
        leaderboard.length === 0 ? (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
            <Trophy className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
              No activity yet this week
            </h3>
            <p className="text-sm text-slate-400">
              Start earning points to appear on the leaderboard!
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((user) => (
              <LeaderboardRow
                key={user.user_id}
                user={user}
                rank={user.rank}
                isCurrentUser={user.user_id === currentUserId}
              />
            ))}
          </div>
        )
      ) : activeTab === "winners" ? (
        pastWinners.length === 0 ? (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
            <Crown className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
              No winners announced yet
            </h3>
            <p className="text-sm text-slate-400">
              Winners will be announced at the end of each week!
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {pastWinners.map((weekData) => (
              <div key={weekData.week_key} className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-bold text-white">{weekData.week_key}</h4>
                  <span className="text-xs text-slate-500">
                    {new Date(weekData.announced_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="space-y-3">
                  {weekData.winners?.map((winner) => (
                    <div
                      key={winner.place}
                      className={`flex items-center gap-4 p-3 rounded-xl ${
                        winner.place === 1
                          ? "bg-[#FFD700]/10"
                          : winner.place === 2
                          ? "bg-[#C0C0C0]/10"
                          : "bg-[#CD7F32]/10"
                      }`}
                    >
                      <span className="text-xl">
                        {winner.place === 1 ? "🥇" : winner.place === 2 ? "🥈" : "🥉"}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{winner.name}</p>
                        <p className="text-xs text-slate-500">{winner.points} points</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-400">{winner.prize_title}</p>
                        {winner.prize_value && winner.prize_value !== "TBD" && (
                          <p className="text-sm font-bold text-[#00FF88]">{winner.prize_value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : activeTab === "history" ? (
        myPoints?.history?.length === 0 ? (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-12 text-center">
            <Star className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Manrope" }}>
              No points earned yet
            </h3>
            <p className="text-sm text-slate-400">
              Complete activities to start earning points!
            </p>
          </div>
        ) : (
          <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-4">
            {myPoints?.history?.map((item, i) => (
              <PointsHistoryItem key={i} item={item} />
            ))}
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(myPoints?.points_config || {}).map(([action, points]) => {
            const labels = {
              vault_upload: { label: "Upload to Vault", icon: "📤", desc: "Photos, videos, documents" },
              voice_recording: { label: "Record Voice Note", icon: "🎤", desc: "In-app voice recording" },
              video_recording: { label: "Record Video", icon: "📹", desc: "In-app video recording" },
              journal_entry: { label: "Write Journal Entry", icon: "📝", desc: "Personal reflections" },
              prompt_response: { label: "Answer Daily Prompt", icon: "💭", desc: "Daily reflection prompts" },
              meal_upload: { label: "Log a Meal", icon: "🍽️", desc: "Track your nutrition" },
              share_to_community: { label: "Share to Community", icon: "🌐", desc: "Share your reflections" },
              receive_reaction: { label: "Receive Reaction", icon: "❤️", desc: "When others react to your posts" },
              daily_streak_bonus: { label: "Daily Streak", icon: "🔥", desc: "Per day of consecutive activity" },
              weekly_streak_bonus: { label: "7-Day Streak", icon: "⭐", desc: "Complete a full week" },
            };
            const info = labels[action] || { label: action, icon: "✨", desc: "" };

            return (
              <div
                key={action}
                className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-xl p-4 flex items-center gap-4"
              >
                <div className="text-2xl">{info.icon}</div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">{info.label}</p>
                  <p className="text-xs text-slate-500">{info.desc}</p>
                </div>
                <div className="text-right">
                  <span className="text-lg font-bold text-[#00FF88]">+{points}</span>
                  <p className="text-xs text-slate-500">pts</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
